// Shared formatting utilities for the trading interface

/**
 * Format time remaining in milliseconds to readable string
 * @param timeMs Time in milliseconds
 * @returns Formatted time string (e.g., "2m 15s" or "45s")
 */
export const formatTimeRemaining = (timeMs: number): string => {
  const totalSeconds = Math.ceil(timeMs / 1000);
  if (totalSeconds >= 60) {
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}m ${seconds}s`;
  }
  return `${totalSeconds}s`;
};

/**
 * Format market cap price for display
 * @param price Market cap value in USD
 * @returns Formatted price string (e.g., "$1.23B", "$456.78M", "$123.4K")
 */
export const formatMarketCap = (price: number): string => {
  if (price >= 1_000_000_000) {
    return `$${(price / 1_000_000_000).toFixed(2)}B`;
  } else if (price >= 1_000_000) {
    return `$${(price / 1_000_000).toFixed(2)}M`;
  } else if (price >= 1_000) {
    return `$${(price / 1_000).toFixed(1)}K`;
  } else {
    return `$${price.toFixed(2)}`;
  }
};

/**
 * Get timer status for styling based on time remaining
 * @param timeMs Time in milliseconds
 * @returns Timer status string for CSS styling
 */
export const getTimerStatus = (timeMs: number): 'critical' | 'warning' | 'normal' => {
  const seconds = Math.ceil(timeMs / 1000);
  if (seconds <= 30) return 'critical';
  if (seconds <= 60) return 'warning';
  return 'normal';
};

/**
 * Format SOL amount for display
 * @param sol SOL amount
 * @param decimals Number of decimal places (default: 3)
 * @returns Formatted SOL string
 */
export const formatSolAmount = (sol: number, decimals: number = 3): string => {
  return sol.toFixed(decimals);
};

/**
 * Format profit/loss percentage
 * @param percentage Percentage value
 * @param includeSign Whether to include + sign for positive values
 * @returns Formatted percentage string
 */
export const formatPercentage = (percentage: number, includeSign: boolean = true): string => {
  const sign = includeSign && percentage > 0 ? '+' : '';
  return `${sign}${percentage.toFixed(2)}%`;
};

/**
 * Format duration in milliseconds to human-readable format
 * @param ms Duration in milliseconds
 * @returns Formatted duration string (e.g., "2d 5h", "5h 30m", "30m", "45s")
 */
export const formatDuration = (ms: number): string => {
  const seconds = Math.floor(ms / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (days > 0) {
    return `${days}d ${hours % 24}h`;
  }
  if (hours > 0) {
    return `${hours}h ${minutes % 60}m`;
  }
  if (minutes > 0) {
    return `${minutes}m`;
  }
  return `${seconds}s`;
};

/**
 * Format time until a future date
 * @param futureDate Future date (Date object or ISO string)
 * @returns Formatted time string (e.g., "5h 30m left", "Ended")
 */
export const formatTimeUntil = (futureDate: Date | string): string => {
  const now = Date.now();
  const future = typeof futureDate === 'string' ? new Date(futureDate).getTime() : futureDate.getTime();
  const diff = future - now;

  if (diff <= 0) {
    return 'Ended';
  }

  return formatDuration(diff);
};
